import { Link } from "react-router-dom";
import { ContactForm } from "./Contact";
import heroImg from "../../assets/images/pe-hero-cast.jpg";
import { useRef, useState, useEffect } from "react";
import { brandingConfig } from "../../config/branding";
import useUserStore from "../../stores/user";
import VideoModal from "../modals/VideoModal";
import { useT } from "../../stores/uiLang";

const LANDING_T = {
  en: {
    badge: "Bilingual Literacy for K-5",
    h1: "Bilingual literacy through story",
    heroP:
      "K-5 bilingual literacy in English and Spanish, built around animated story episodes at four reading levels, anchored in the Science of Reading's knowledge and vocabulary strand.",
    overview: "Program Overview",
    explore: "Explore Curriculum",
    bookDemo: "Book a Demo",
    charactersAlt: (site: string) => `${site} characters`,
    storyTitle: "Story-Driven Learning",
    storyP: "Animated episodes at four reading levels, in English and Spanish.",
    whyH: "Why Schools Choose Project Explore",
    whyP: "Project Explore is a K-5 bilingual literacy program that uses story to teach how money, work, and value function — composed natively in English and Spanish, at four reading levels, built for the dual-language classroom you actually have.",
    gapH: "Fills the Standards Gap",
    gapP: "Most states lack clear K-5 financial-literacy mandates. We provide the structured, standards-aligned roadmap so you don't have to build it.",
    zeroH: "Zero-Prep for Teachers",
    zeroP: "Click-and-teach lesson plans in English and Spanish. No prior training, no evening prep.",
    diffH: "Differentiation Built-In",
    diffP: "Four scaffolded reading tiers per chapter, so every reader in a mixed-ability classroom succeeds.",
    meet: "Meet the Friends",
    charAlt: (n: string) => `${n} — a Project Explore character`,
    builtH: "Built for Teacher-Led Instruction",
    builtP: "Designed around how real classrooms run.",
    readH: "Read-Aloud Storytelling",
    readP: "Animated story episodes with synchronized word-by-word text highlighting, built for teacher-led read-alouds.",
    guidesH: "Bilingual Teacher Guides",
    guidesP: "Step-by-step daily lesson plans in English and Spanish — objectives, vocabulary, and standards alignment.",
    extH: "Extension Activities",
    extP: "Hands-on projects in 20, 30, or 45-minute formats across three budget tiers — adapts to any classroom.",
    oneH: "One Story. Four Reading Levels. Two Languages.",
    oneP: "Every chapter exists at four tiers, Kindergarten through 5th grade — same narrative, same characters, escalating language complexity and art. A multi-grade classroom reads the same story together, each child at their level.",
    stripP: "The same Chapter 1 scene, illustrated for four reading levels.",
    level: (n: number) => `Level ${n}`,
    stripAlt: (level: string) => `Chapter 1 ${level} illustration`,
    nativeH: "Composed Natively in Spanish",
    nativeP: "The Spanish isn't a translation of the English — both languages were written from the ground up.",
    litH: "A Literacy Program First",
    litP: "Builds vocabulary and knowledge — the comprehension strand of the Science of Reading. It competes for ELA time.",
    tlH: "Teacher-Led, Not Another App",
    tlP: "Four-tier differentiation, gradual-release design, and bilingual read-along, built around real classroom delivery.",
    closeVideo: "Close video",
  },
  es: {
    badge: "Lectoescritura bilingüe de K a 5.º grado",
    h1: "Lectoescritura bilingüe a través de cuentos",
    heroP:
      "Lectoescritura bilingüe de K a 5.º grado en inglés y español, creada con episodios animados de cuentos en cuatro niveles de lectura y apoyada en el eje de conocimiento y vocabulario de la ciencia de la lectura.",
    overview: "Resumen del programa",
    explore: "Explorar el currículo",
    bookDemo: "Reservar una demostración",
    charactersAlt: (site: string) => `Personajes de ${site}`,
    storyTitle: "Aprendizaje basado en cuentos",
    storyP: "Episodios animados en cuatro niveles de lectura, en inglés y español.",
    whyH: "Por qué las escuelas eligen Project Explore",
    whyP: "Project Explore es un programa de lectoescritura bilingüe de K a 5.º grado que usa cuentos para enseñar cómo funcionan el dinero, el trabajo y el valor. Está escrito de forma nativa en inglés y español, en cuatro niveles de lectura, para el salón de lenguaje dual que realmente tienen.",
    gapH: "Llena el vacío en los estándares",
    gapP: "La mayoría de los estados no tiene requisitos claros de educación financiera para K a 5.º grado. Ofrecemos la ruta estructurada y alineada con los estándares para que ustedes no tengan que crearla.",
    zeroH: "Sin preparación para los maestros",
    zeroP: "Planes de lección listos para usar, en inglés y español. Sin capacitación previa y sin preparación por las tardes.",
    diffH: "Diferenciación incluida",
    diffP: "Cuatro niveles de lectura con apoyos graduales en cada capítulo, para que todos los lectores de un salón con habilidades diversas tengan éxito.",
    meet: "Conozca a los amigos",
    charAlt: (n: string) => `${n}, personaje de Project Explore`,
    builtH: "Hecho para la enseñanza dirigida por el maestro",
    builtP: "Diseñado según cómo funcionan los salones reales.",
    readH: "Cuentos para leer en voz alta",
    readP: "Episodios animados con el texto resaltado palabra por palabra en sincronía, pensados para lecturas en voz alta dirigidas por el maestro.",
    guidesH: "Guías bilingües para el maestro",
    guidesP: "Planes de lección diarios, paso a paso, en inglés y español: objetivos, vocabulario y alineación con los estándares.",
    extH: "Actividades de extensión",
    extP: "Proyectos prácticos en formatos de 20, 30 o 45 minutos, con tres niveles de presupuesto. Se adaptan a cualquier salón.",
    oneH: "Un cuento. Cuatro niveles de lectura. Dos idiomas.",
    oneP: "Cada capítulo existe en cuatro niveles, desde kínder hasta 5.º grado: la misma narrativa, los mismos personajes, con un lenguaje y unas ilustraciones cada vez más complejos. Un salón de varios grados lee el mismo cuento junto, cada niño en su nivel.",
    stripP: "La misma escena del Capítulo 1, ilustrada para cuatro niveles de lectura.",
    level: (n: number) => `Nivel ${n}`,
    stripAlt: (level: string) => `Ilustración del Capítulo 1, ${level}`,
    nativeH: "Escrito de forma nativa en español",
    nativeP: "El español no es una traducción del inglés: ambos idiomas se escribieron desde cero.",
    litH: "Primero, un programa de lectoescritura",
    litP: "Desarrolla vocabulario y conocimiento, el eje de comprensión de la ciencia de la lectura. Compite por el tiempo de artes del lenguaje (ELA).",
    tlH: "Dirigido por el maestro, no otra aplicación",
    tlP: "Diferenciación en cuatro niveles, diseño de liberación gradual y lectura acompañada bilingüe, pensados para la enseñanza real en el salón.",
    closeVideo: "Cerrar video",
  },
};

const Landing = () => {
  const t = useT(LANDING_T);
  const bookCallpRef = useRef<HTMLDivElement>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && setShowVideo(false);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const handleScroll = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenVideo = (video: any) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  const { profile } = useUserStore();

  const programOverviewVideo = {
    videoUrl:
      "https://pub-adda9ed2b8b64c2b94e6f0bd9fbee119.r2.dev/instructor-support-videos/Sizzle%20Reel.mp4",
  };

  return (
    <>
      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }

        .rotate-y-2 {
          transform: rotateY(2deg);
        }

        .rotate-x-5 {
          transform: rotateX(5deg);
        }

        .rotate-z-1 {
          transform: rotateZ(1deg);
        }

        .transform {
          transform-style: preserve-3d;
        }

        @keyframes float {
          0% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(10px, 10px) rotate(5deg); }
          50% { transform: translate(0, 20px) rotate(0deg); }
          75% { transform: translate(-10px, 10px) rotate(-5deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }

        .floating-icon {
          position: absolute;
          animation: float infinite linear;
          opacity: 0.2;
          color: white;
          font-size: 3rem;
          pointer-events: none;
        }
      `}</style>
      {/* Spacer for fixed header */}
      <div className="h-20" />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#f4f7f2] to-white py-20 md:py-24 lg:py-28">
        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex text-[#3b5839] items-center rounded-full border border-[#3b5839]/30 px-4 py-1.5 text-sm mb-6">
                <span className="mr-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                  </svg>
                </span>
                <span>{t.badge}</span>
              </div>

              <h1 className="text-4xl text-[#3b5839] font-bold tracking-tight md:text-5xl lg:text-6xl">
                {t.h1}
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-2xl">
                {t.heroP}
              </p>

              <div className="mt-10 flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => setShowVideo(true)}
                    className="btn !w-48 !bg-[#3b5839] !text-white hover:!bg-[#2d4428] !border-transparent"
                  >
                    {t.overview}
                  </button>
                  <Link
                    to="/standards"
                    className="btn !w-48 !bg-transparent !text-[#3b5839] !border !border-[#3b5839] hover:!bg-[#3b5839]/5"
                  >
                    {t.explore}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-1"
                    >
                      <path d="M5 12h14"></path>
                      <path d="m12 5 7 7-7 7"></path>
                    </svg>
                  </Link>
                </div>
                {(!profile || profile.role !== "student") && (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => handleScroll(bookCallpRef)}
                      className="btn !w-48 !bg-[#3b5839] !text-white hover:!bg-[#2d4428] !border-transparent"
                    >
                      {t.bookDemo}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="relative">
              <div className="relative">
                <div className="relative z-10 rounded-2xl border p-4 shadow-xl">
                  <div className="overflow-hidden rounded-xl">
                    <img
                      src={heroImg}
                      alt={t.charactersAlt(brandingConfig.siteTitle)}
                      className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-700"
                    />
                  </div>

                  <div className="mt-6 flex items-center justify-between bg-gradient-to-r from-[#3b5839]/10 to-[#3b5839]/5 border p-4 rounded-xl">
                    <div className="flex items-center">
                      <div className="rounded-full accent-bg p-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="cover-primary-text"
                        >
                          <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                          <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                        </svg>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-base text-[#3b5839] font-semibold">
                          {t.storyTitle}
                        </h4>
                        <p className="text-sm secondary-text">
                          {t.storyP}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[var(--color-background)] py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold  lg:text-5xl">
              {t.whyH}
            </h1>
            <p className="mt-4 text-xl secondary-text mx-auto max-w-3xl">
              {t.whyP}
            </p>
          </div>
          <div className="mx-auto mt-8 grid max-w-6xl gap-8 md:mt-16 md:grid-cols-3 lg:grid-cols-3">
            <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
              <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-gradient"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at center, transparent 30%, white 100%)",
                    mixBlendMode: "overlay",
                  }}
                ></div>

                <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon-color"
                  >
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                  </svg>
                </div>
              </div>
              <h3 className="mt-8 text-xl font-semibold primary-text">
                {t.gapH}
              </h3>
              <p className="mt-3 text-base secondary-text leading-relaxed">
                {t.gapP}
              </p>
            </div>

            <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
              <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-gradient"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at center, transparent 30%, white 100%)",
                    mixBlendMode: "overlay",
                  }}
                ></div>

                <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon-color"
                  >
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                    <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                  </svg>
                </div>
              </div>
              <h3 className="mt-8 text-xl font-semibold primary-text">
                {t.zeroH}
              </h3>
              <p className="mt-3 text-base secondary-text leading-relaxed">
                {t.zeroP}
              </p>
            </div>

            <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
              <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-gradient"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at center, transparent 30%, white 100%)",
                    mixBlendMode: "overlay",
                  }}
                ></div>

                <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon-color"
                  >
                    <path d="M3 3v18h18"></path>
                    <path d="m19 9-5 5-4-4-3 3"></path>
                  </svg>
                </div>
              </div>
              <h3 className="mt-8 text-xl font-semibold primary-text">
                {t.diffH}
              </h3>
              <p className="mt-3 text-base secondary-text leading-relaxed">
                {t.diffP}
              </p>
            </div>
          </div>

          {/* Meet the Friends */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold lg:text-5xl" style={{ color: '#3b5839' }}>
              {t.meet}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex flex-col items-center">
              <div className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-lg" style={{ aspectRatio: '3/4' }}>
                <img
                  src="https://api.startupsmartup.com/uploads/CH_1_SC_07_tier_3_e89ff13de1.png"
                  alt={t.charAlt("Layla")}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="300"
                  height="400"
                />
              </div>
              <p className="mt-3 text-lg font-semibold" style={{ color: '#3b5839' }}>Layla</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-lg" style={{ aspectRatio: '3/4' }}>
                <img
                  src="https://api.startupsmartup.com/uploads/CH_1_SC_13_tier_3_fc287281b9.png"
                  alt={t.charAlt("Riley")}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="300"
                  height="400"
                />
              </div>
              <p className="mt-3 text-lg font-semibold" style={{ color: '#3b5839' }}>Riley</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-lg" style={{ aspectRatio: '3/4' }}>
                <img
                  src="https://raw.githubusercontent.com/jghiglia2380/project-explore-thumbnails/main/CH1-SC08%20tier%203.png"
                  alt={t.charAlt("Ellis")}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="300"
                  height="400"
                />
              </div>
              <p className="mt-3 text-lg font-semibold" style={{ color: '#3b5839' }}>Ellis</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-lg" style={{ aspectRatio: '3/4' }}>
                <img
                  src="https://api.startupsmartup.com/uploads/CH_1_SC_09_tier_3_61f3d69cc9.png"
                  alt={t.charAlt("Benny")}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="300"
                  height="400"
                />
              </div>
              <p className="mt-3 text-lg font-semibold" style={{ color: '#3b5839' }}>Benny</p>
            </div>
          </div>
        </div>

        <div className="mt-24">
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold primary-text lg:text-5xl">
                {t.builtH}
              </h1>
              <p className="mt-4 text-xl secondary-text mx-auto max-w-3xl">
                {t.builtP}
              </p>
            </div>

            <div className="mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
                <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                  ></div>

                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-full bg-gradient"
                  ></div>

                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-full"
                    style={{
                      background:
                        "radial-gradient(circle at center, transparent 30%, white 100%)",
                      mixBlendMode: "overlay",
                    }}
                  ></div>

                  <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon-color"
                    >
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                    </svg>
                  </div>
                </div>
                <h3 className="mt-8 text-xl font-semibold primary-text">
                  {t.readH}
                </h3>
                <p className="mt-3 text-base secondary-text leading-relaxed">
                  {t.readP}
                </p>
              </div>

              <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
                <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                  ></div>

                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-full bg-gradient"
                  ></div>

                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-full"
                    style={{
                      background:
                        "radial-gradient(circle at center, transparent 30%, white 100%)",
                      mixBlendMode: "overlay",
                    }}
                  ></div>

                  <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon-color"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                  </div>
                </div>
                <h3 className="mt-8 text-xl font-semibold primary-text">
                  {t.guidesH}
                </h3>
                <p className="mt-3 text-base secondary-text leading-relaxed">
                  {t.guidesP}
                </p>
              </div>

              <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
                <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                  ></div>

                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-full bg-gradient"
                  ></div>

                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-full"
                    style={{
                      background:
                        "radial-gradient(circle at center, transparent 30%, white 100%)",
                      mixBlendMode: "overlay",
                    }}
                  ></div>

                  <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon-color"
                    >
                      <path d="M12 20h9"></path>
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                    </svg>
                  </div>
                </div>
                <h3 className="mt-8 text-xl font-semibold primary-text">
                  {t.extH}
                </h3>
                <p className="mt-3 text-base secondary-text leading-relaxed">
                  {t.extP}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Student Growth and Application of Knowledge */}
      <div className="bg-[var(--color-background)] pb-16 md:pb-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold primary-text lg:text-5xl">
              {t.oneH}
            </h1>
            <p className="mt-4 text-xl secondary-text mx-auto max-w-3xl">
              {t.oneP}
            </p>
          </div>

          {/* Chapter 1 tier strip */}
          <div className="mx-auto mt-12 max-w-6xl">
            <p className="text-center text-base secondary-text mb-8">
              {t.stripP}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { src: 'https://api.startupsmartup.com/uploads/Ch_01_t1_e9eb85e4d5.jpeg', n: 1 },
                { src: 'https://api.startupsmartup.com/uploads/Ch_01_t2_585757719e.jpeg', n: 2 },
                { src: 'https://api.startupsmartup.com/uploads/Ch_01_t3_cb49102ef7.jpeg', n: 3 },
                { src: 'https://api.startupsmartup.com/uploads/Ch_01_t4_8a8dbef079.jpeg', n: 4 },
              ].map(({ src, n }) => (
                <div key={n} className="flex flex-col items-center">
                  <div className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-lg" style={{ aspectRatio: '4/3' }}>
                    <img
                      src={src}
                      alt={t.stripAlt(t.level(n))}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width="400"
                      height="300"
                    />
                  </div>
                  <p className="mt-2 text-sm font-semibold" style={{ color: '#3b5839' }}>{t.level(n)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mx-auto mt-16 grid max-w-6xl gap-8 md:grid-cols-3 lg:grid-cols-3">
            <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
              <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-gradient"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at center, transparent 30%, white 100%)",
                    mixBlendMode: "overlay",
                  }}
                ></div>

                <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon-color"
                  >
                    <rect
                      width="18"
                      height="18"
                      x="3"
                      y="3"
                      rx="2"
                      ry="2"
                    ></rect>
                    <circle cx="9" cy="9" r="2"></circle>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                  </svg>
                </div>
              </div>
              <h3 className="mt-8 text-xl font-semibold primary-text">
                {t.nativeH}
              </h3>
              <p className="mt-3 text-base secondary-text leading-relaxed">
                {t.nativeP}
              </p>
            </div>

            <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
              <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-gradient"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at center, transparent 30%, white 100%)",
                    mixBlendMode: "overlay",
                  }}
                ></div>

                <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon-color"
                  >
                    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path>
                    <path d="M13 5v2"></path>
                    <path d="M13 17v2"></path>
                    <path d="M13 11v2"></path>
                  </svg>
                </div>
              </div>
              <h3 className="mt-8 text-xl font-semibold primary-text">
                {t.litH}
              </h3>
              <p className="mt-3 text-base secondary-text leading-relaxed">
                {t.litP}
              </p>
            </div>

            <div className="group rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg transition-all duration-300 hover:shadow-xl">
              <div className="relative mx-auto h-36 w-36 duration-200 overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 bg-[#3b5839]/5 rounded-full"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-gradient"
                ></div>

                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at center, transparent 30%, white 100%)",
                    mixBlendMode: "overlay",
                  }}
                ></div>

                <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon-color"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
              </div>
              <h3 className="mt-8 text-xl primary-text font-semibold">
                {t.tlH}
              </h3>
              <p className="mt-3 text-base secondary-text leading-relaxed">
                {t.tlP}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      {(!profile || profile.role !== "student") && (
        <div
          className="pb-16 md:pb-24 bg-[var(--color-background)]"
          ref={bookCallpRef}
        >
          <ContactForm />
        </div>
      )}

      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        video={selectedVideo}
      />

      {showVideo && (
        <div
          onClick={() => setShowVideo(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl aspect-video"
          >
            <button
              onClick={() => setShowVideo(false)}
              aria-label={t.closeVideo}
              className="absolute -top-10 right-0 text-white text-3xl leading-none"
            >
              ×
            </button>
            <iframe
              src="https://player.vimeo.com/video/1202335933?autoplay=1&title=0&byline=0&portrait=0"
              className="w-full h-full rounded-lg"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Landing;
